// useMediaUI.js - Hook UI genérico para recursos multimedia (imagen, video, documento, sticker, etc.)
import { useMemo, useCallback, useEffect } from 'react';
import useRetryCounter from '../useRetryCounter';
import { buildSafeIdentifier, buildMediaKey } from '../useSafeMediaKey';
import useFbMsnUrl from '../useFbMsnUrl';
import { encodeWhatsAppMediaUrl } from '../../../../util/mediaUrlUtils';
import { isWhatsAppMsPlatform } from '../../../../services/inbox/inboxMsChatPlatform';

/**
 * Hook de UI reutilizable para mensajes multimedia.
 * Gestiona reintentos, fallback visual, keys y lógica de error adaptable por plataforma/tipo.
 *
 * @param {Object} params
 * @param {string} params.platform - Plataforma ("whatsapp", "messenger", etc.)
 * @param {string} params.url - URL del recurso multimedia
 * @param {Object} params.data - Objeto de datos multimedia. Debe incluir:
 *   - id: string|number - Identificador único del mensaje
 *   - accountUID: string|number - Identificador de cuenta (para key)
 * @param {function} params.setUrl - Setter para la URL (opcional)
 * @param {function} params.setError - Setter para error visual
 * @param {number} params.numberOfAttempts - Número máximo de reintentos
 * @param {string} params.mediaType - Tipo de media ("image", "video", "document", etc.)
 * @param {function} params.onPlatformRetry - Callback especial para lógica de reintento por plataforma (opcional)
 * @returns {Object}
 */
export default function useMediaUI({
  platform,
  url,
  data,
  setUrl,
  setError,
  numberOfAttempts = 3,
  setLoading,
  mediaType = 'media',
  onPlatformRetry,
}) {
  // Reintentos y fallback reutilizable
  const {
    retryCounter,
    lastChance,
    increment,
    setLastChance
  } = useRetryCounter({ maxAttempts: numberOfAttempts });


  // Hook compartido para obtener la URL de Messenger
  const fbMsnUrlFetcher = useFbMsnUrl();
  const getFbMsnUrl = useCallback(async () => {
    return fbMsnUrlFetcher(data);
  }, [fbMsnUrlFetcher, data]);

  // Handler de error genérico adaptable
  const handleMediaError = useCallback(async (event) => {
    // Mientras no se haya alcanzado el máximo de intentos normales
    if (retryCounter < numberOfAttempts) {
      if (!isWhatsAppMsPlatform(platform)) {
        if (onPlatformRetry) {
          await onPlatformRetry(platform, { event, getFbMsnUrl });
        } else {
          try {
            if (retryCounter < 1) {
              const resultUrl = await getFbMsnUrl();
              setUrl(resultUrl);
            }
          } catch (e) {
            setError(true);
          }
        }
        increment();
        return;
      }
      // Otros casos de plataforma
      increment();
      onPlatformRetry?.(platform, { event });
      return;
    }

    if (!lastChance) {
      setLastChance(true);
      return;
    }
    setLoading?.(false);
    setError?.(true);
  }, [retryCounter, lastChance, platform, data, url, setUrl, setError, numberOfAttempts, increment, setLastChance, onPlatformRetry, setLoading, getFbMsnUrl]);

  // Identificador seguro y key reutilizable
  const safeIdentifier = useMemo(() => buildSafeIdentifier(data?.accountUID || data?.identifier), [data?.accountUID, data?.identifier]);
  const mediaKey = useMemo(() => buildMediaKey({
    prefix: mediaType,
    messageIdentifier: data?.id,
    accountUID: safeIdentifier,
    counter: retryCounter,
    lastChance
  }), [mediaType, data?.id, safeIdentifier, retryCounter, lastChance]);
  const mediaSrc = useMemo(() => (lastChance ? url : encodeWhatsAppMediaUrl(url)), [lastChance, url]);

  // Initial fetch for non-whatsapp platforms if URL is missing
  useEffect(() => {
    const fetchInitialUrl = async () => {
      if (!url && !isWhatsAppMsPlatform(platform) && (data?.mid || data?.identifier)) {
        setLoading?.(true);
        try {
          const resultUrl = await getFbMsnUrl();
          if (resultUrl) {
            setUrl(resultUrl);
          } else {
            setError?.(true);
          }
        } catch (e) {
          setError?.(true);
        } finally {
          setLoading?.(false);
        }
      }
    };

    fetchInitialUrl();
  }, [url, platform, data?.mid, data?.identifier, getFbMsnUrl, setUrl, setError, setLoading]);

  return useMemo(() => ({
    lastChance,
    retryCounter,
    handleMediaError,
    safeIdentifier,
    mediaKey,
    mediaSrc,
    setLastChance
  }), [lastChance, retryCounter, handleMediaError, safeIdentifier, mediaKey, mediaSrc, setLastChance]);
}
